/**
 * Loan Planning Page - Auxilio Finance
 * Loan application package builder with checklists, calculators, and letter generation
 */

import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ClockIcon,
  DocumentDuplicateIcon,
  CalculatorIcon,
  ScaleIcon,
  TrashIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  TruckIcon,
  AcademicCapIcon,
  BanknotesIcon,
  BuildingOffice2Icon,
  BuildingLibraryIcon,
  ClipboardDocumentCheckIcon,
  PencilIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import apiClient from '../services/api';
import { Button } from '../components/ui/Button';
import { ExportDropdownMenu } from '../components/ui/ExportDropdownMenu';
import { useThemeStore } from '../store/themeStore';

interface LoanType {
  id: string;
  name: string;
  description: string;
  typical_term_years: number[];
  typical_apr_range: string;
  icon: string;
}

interface DocumentItem {
  id: string;
  name: string;
  required: boolean;
  status?: string;
  status_notes?: string;
}

interface DocumentCategory {
  id: string;
  name: string;
  category: string;
  items: DocumentItem[];
}

interface LoanApplication {
  id: string;
  loan_type: string;
  loan_amount: number;
  loan_purpose?: string;
  property_value?: number;
  down_payment?: number;
  annual_income: number;
  monthly_debt_payments: number;
  credit_score_estimate?: number;
  notes?: string;
  current_dti?: number;
  ltv?: number;
  status: string;
  created_at: string;
  document_progress?: {
    total: number;
    completed: number;
    percentage: number;
  };
}

interface LenderComparison {
  id: string;
  lender_name: string;
  interest_rate: number;
  loan_term_months: number;
  fees: number;
  points: number;
  point_cost: number;
  monthly_payment: number;
  total_interest: number;
  total_cost: number;
  notes?: string;
  rank?: number;
}

interface PreQualification {
  loan_type: string;
  estimated_rate: number;
  max_loan_amount: number;
  max_monthly_payment: number;
  current_dti: number;
  ltv?: number;
  approval_factors: Array<{
    factor: string;
    status: string;
    message: string;
  }>;
  recommendations: string[];
}

interface LetterTemplate {
  id: string;
  name: string;
  description: string;
}

const LOAN_TYPE_ICONS: Record<string, React.ElementType> = {
  'mortgage_purchase': HomeIcon,
  'mortgage_refinance': HomeIcon,
  'auto_loan': TruckIcon,
  'personal_loan': BanknotesIcon,
  'student_loan_refinance': AcademicCapIcon,
  'home_equity': BuildingOffice2Icon,
  'heloc': BuildingLibraryIcon,
  'small_business': BuildingOffice2Icon,
};

// Fields that should use date picker
const DATE_FIELDS = [
  'start_date',
  'item_date',
  'gap_start_date',
  'gap_end_date',
  'current_start_date',
];

export const LoanPlanning: React.FC = () => {
  const { t } = useTranslation(['loanPlanning', 'common']);
  const theme = useThemeStore((state) => state.theme);

  // Determine if we're in dark mode for date input styling
  const isDarkMode = theme === 'dark' ||
    (theme === 'system' && typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches);
  const dateInputStyle: React.CSSProperties = { colorScheme: isDarkMode ? 'dark' : 'light' };

  // State
  const [activeTab, setActiveTab] = useState<'applications' | 'prequal' | 'letters'>('applications');
  const [loanTypes, setLoanTypes] = useState<LoanType[]>([]);
  const [applications, setApplications] = useState<LoanApplication[]>([]);
  const [selectedApp, setSelectedApp] = useState<LoanApplication | null>(null);
  const [checklist, setChecklist] = useState<DocumentCategory[]>([]);
  const [lenderComparisons, setLenderComparisons] = useState<LenderComparison[]>([]);
  const [letterTemplates, setLetterTemplates] = useState<LetterTemplate[]>([]);
  const [preQualResult, setPreQualResult] = useState<PreQualification | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Modals
  const [showNewAppModal, setShowNewAppModal] = useState(false);
  const [showLenderModal, setShowLenderModal] = useState(false);
  const [showLetterModal, setShowLetterModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);

  // Form state
  const [newAppForm, setNewAppForm] = useState({
    loan_type: '',
    loan_amount: '',
    loan_purpose: '',
    property_value: '',
    down_payment: '',
    annual_income: '',
    monthly_debt_payments: '',
    credit_score_estimate: '',
    notes: '',
  });

  const [lenderForm, setLenderForm] = useState({
    lender_name: '',
    interest_rate: '',
    loan_term_months: '',
    fees: '',
    points: '',
    notes: '',
  });

  const [preQualForm, setPreQualForm] = useState({
    loan_type: 'mortgage_purchase',
    annual_income: '',
    monthly_debt_payments: '',
    credit_score: '',
    down_payment: '',
    property_value: '',
  });

  const [selectedLetterType, setSelectedLetterType] = useState<string | null>(null);
  const [letterFields, setLetterFields] = useState<Record<string, string>>({});
  const [generatedLetter, setGeneratedLetter] = useState<string | null>(null);
  const [letterError, setLetterError] = useState<string | null>(null);
  const [generatingLetter, setGeneratingLetter] = useState(false);

  // Fetch data on mount
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [typesRes, appsRes, templatesRes] = await Promise.all([
        apiClient.get('/loan-planning/loan-types'),
        apiClient.get('/loan-planning/applications'),
        apiClient.get('/loan-planning/letter-templates'),
      ]);

      setLoanTypes(typesRes.data.loan_types || []);
      setApplications(appsRes.data.applications || []);
      setLetterTemplates(templatesRes.data.templates || []);
    } catch (err) {
      setError('Failed to load loan planning data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchApplicationDetails = async (appId: string) => {
    try {
      const response = await apiClient.get(`/loan-planning/applications/${appId}`);
      setSelectedApp(response.data.application);
      setChecklist(response.data.checklist || []);
      setLenderComparisons(response.data.lender_comparisons || []);
      setExpandedCategories(response.data.checklist?.map((c: DocumentCategory) => c.id) || []);
    } catch (err) {
      console.error('Failed to fetch application details:', err);
    }
  };

  const handleCreateApplication = async () => {
    try {
      const payload = {
        loan_type: newAppForm.loan_type,
        loan_amount: parseFloat(newAppForm.loan_amount),
        loan_purpose: newAppForm.loan_purpose || undefined,
        property_value: newAppForm.property_value ? parseFloat(newAppForm.property_value) : undefined,
        down_payment: newAppForm.down_payment ? parseFloat(newAppForm.down_payment) : undefined,
        annual_income: parseFloat(newAppForm.annual_income),
        monthly_debt_payments: parseFloat(newAppForm.monthly_debt_payments) || 0,
        credit_score_estimate: newAppForm.credit_score_estimate ? parseInt(newAppForm.credit_score_estimate) : undefined,
        notes: newAppForm.notes || undefined,
      };

      await apiClient.post('/loan-planning/applications', payload);
      setShowNewAppModal(false);
      setNewAppForm({
        loan_type: '',
        loan_amount: '',
        loan_purpose: '',
        property_value: '',
        down_payment: '',
        annual_income: '',
        monthly_debt_payments: '',
        credit_score_estimate: '',
        notes: '',
      });
      fetchData();
    } catch (err) {
      console.error('Failed to create application:', err);
    }
  };

  const handleUpdateDocumentStatus = async (docId: string, status: string) => {
    if (!selectedApp) return;

    try {
      await apiClient.put(`/loan-planning/applications/${selectedApp.id}/document-status`, {
        document_id: docId,
        status: status,
      });
      fetchApplicationDetails(selectedApp.id);
    } catch (err) {
      console.error('Failed to update document status:', err);
    }
  };

  const handleAddLender = async () => {
    if (!selectedApp) return;

    try {
      const payload = {
        lender_name: lenderForm.lender_name,
        interest_rate: parseFloat(lenderForm.interest_rate),
        loan_term_months: parseInt(lenderForm.loan_term_months),
        fees: parseFloat(lenderForm.fees) || 0,
        points: parseFloat(lenderForm.points) || 0,
        notes: lenderForm.notes || undefined,
      };

      await apiClient.post(`/loan-planning/applications/${selectedApp.id}/compare-lenders`, payload);
      setShowLenderModal(false);
      setLenderForm({
        lender_name: '',
        interest_rate: '',
        loan_term_months: '',
        fees: '',
        points: '',
        notes: '',
      });
      fetchApplicationDetails(selectedApp.id);
    } catch (err) {
      console.error('Failed to add lender:', err);
    }
  };

  const handleDeleteLender = async (comparisonId: string) => {
    if (!selectedApp) return;

    try {
      await apiClient.delete(`/loan-planning/applications/${selectedApp.id}/compare-lenders/${comparisonId}`);
      fetchApplicationDetails(selectedApp.id);
    } catch (err) {
      console.error('Failed to delete lender:', err);
    }
  };

  const handlePreQualification = async () => {
    try {
      const payload = {
        loan_type: preQualForm.loan_type,
        annual_income: parseFloat(preQualForm.annual_income),
        monthly_debt_payments: parseFloat(preQualForm.monthly_debt_payments) || 0,
        credit_score: preQualForm.credit_score ? parseInt(preQualForm.credit_score) : undefined,
        down_payment: preQualForm.down_payment ? parseFloat(preQualForm.down_payment) : undefined,
        property_value: preQualForm.property_value ? parseFloat(preQualForm.property_value) : undefined,
      };

      const response = await apiClient.post('/loan-planning/pre-qualification', payload);
      setPreQualResult(response.data);
    } catch (err) {
      console.error('Failed to calculate pre-qualification:', err);
    }
  };

  const handleGenerateLetter = async () => {
    if (!selectedLetterType) return;

    setLetterError(null);
    setGeneratingLetter(true);

    try {
      const response = await apiClient.post('/loan-planning/generate-letter', {
        letter_type: selectedLetterType,
        fields: letterFields,
      });
      setGeneratedLetter(response.data.content);
    } catch (err: any) {
      console.error('Failed to generate letter:', err);
      // Extract error message from response if available
      const errorDetail = err.response?.data?.detail || t('errors.letterFailed');
      setLetterError(errorDetail);
    } finally {
      setGeneratingLetter(false);
    }
  };

  const handleLetterExport = async (format: 'word' | 'pdf' | 'text' | 'csv') => {
    if (!generatedLetter || !selectedLetterType || format === 'csv') return;

    const letterName = letterTemplates.find(t => t.id === selectedLetterType)?.name || 'Letter';
    const title = `${letterName} - Loan Application`;

    // Export to file via backend
    const formatMap: Record<string, string> = {
      'word': 'docx',
      'pdf': 'pdf',
      'text': 'txt',
    };

    try {
      const response = await apiClient.post('/export/document', {
        content: generatedLetter,
        title: title,
        format: formatMap[format],
      }, {
        responseType: 'blob',
      });

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}.${formatMap[format]}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export letter:', err);
    }
  };

  const handleLetterPrint = () => {
    if (!generatedLetter || !selectedLetterType) return;

    const letterName = letterTemplates.find(t => t.id === selectedLetterType)?.name || 'Letter';
    const title = `${letterName} - Loan Application`;

    // Open print dialog
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: Arial, sans-serif; padding: 40px; white-space: pre-wrap; }
              h1 { font-size: 18px; margin-bottom: 20px; }
            </style>
          </head>
          <body>
            <h1>${title}</h1>
            <pre>${generatedLetter}</pre>
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.print();
    }
  };

  const handleClearLetterForm = () => {
    setSelectedLetterType(null);
    setLetterFields({});
    setGeneratedLetter(null);
    setLetterError(null);
  };

  const handleDeleteApplication = async (appId: string) => {
    if (!window.confirm(t('confirmDeleteApp'))) return;

    try {
      await apiClient.delete(`/loan-planning/applications/${appId}`);
      if (selectedApp?.id === appId) {
        setSelectedApp(null);
        setChecklist([]);
        setLenderComparisons([]);
      }
      fetchData();
    } catch (err) {
      console.error('Failed to delete application:', err);
    }
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getLoanTypeIcon = (loanType: string) => {
    const Icon = LOAN_TYPE_ICONS[loanType] || DocumentTextIcon;
    return <Icon className="h-5 w-5" />;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'complete':
        return 'text-emerald-600 dark:text-emerald-400';
      case 'in_progress':
        return 'text-amber-600 dark:text-amber-400';
      case 'not_applicable':
        return 'text-gray-400 dark:text-gray-500';
      default:
        return 'text-gray-600 dark:text-gray-400';
    }
  };

  const getApprovalStatusColor = (status: string) => {
    switch (status) {
      case 'good':
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
      case 'fair':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
      case 'concern':
        return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  // Render a letter form field with appropriate input type
  const renderLetterField = (field: string, textareaFields: string[] = []) => {
    const inputClass = "w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm dark:[color-scheme:dark]";

    if (textareaFields.includes(field)) {
      return (
        <textarea
          value={letterFields[field] || ''}
          onChange={(e) => setLetterFields({ ...letterFields, [field]: e.target.value })}
          className={inputClass}
          rows={3}
        />
      );
    }

    if (DATE_FIELDS.includes(field)) {
      return (
        <input
          type="date"
          value={letterFields[field] || ''}
          onChange={(e) => setLetterFields({ ...letterFields, [field]: e.target.value })}
          className={inputClass}
          style={dateInputStyle}
        />
      );
    }

    return (
      <input
        type="text"
        value={letterFields[field] || ''}
        onChange={(e) => setLetterFields({ ...letterFields, [field]: e.target.value })}
        className={inputClass}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('title')}
        </h1>
        <p className="mt-2 text-gray-600 dark:text-gray-400">
          {t('subtitle')}
        </p>
      </div>

      {/* Tabs */}
      <div className="flex space-x-1 mb-6 bg-gray-100 dark:bg-gray-800 p-1 rounded-lg w-fit">
        {[
          { id: 'applications', label: t('tabs.applications'), icon: DocumentDuplicateIcon },
          { id: 'prequal', label: t('tabs.preQualification'), icon: CalculatorIcon },
          { id: 'letters', label: t('tabs.letters'), icon: DocumentTextIcon },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as any)}
            className={`flex items-center px-4 py-2 rounded-md text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'bg-white dark:bg-gray-700 text-emerald-600 dark:text-emerald-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <tab.icon className="h-4 w-4 mr-2" />
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg">
          {error}
        </div>
      )}

      {/* Applications Tab */}
      {activeTab === 'applications' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Applications List */}
          <div className="lg:col-span-1">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-4">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                  {t('yourApplications')}
                </h2>
                <Button
                  onClick={() => setShowNewAppModal(true)}
                  className="flex items-center"
                >
                  <PlusIcon className="h-4 w-4 mr-1" />
                  {t('newApplication')}
                </Button>
              </div>

              {applications.length === 0 ? (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  <DocumentDuplicateIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                  <p>{t('noApplications')}</p>
                  <p className="text-sm mt-1">{t('createFirstApp')}</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {applications.map((app) => (
                    <div
                      key={app.id}
                      onClick={() => fetchApplicationDetails(app.id)}
                      className={`p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedApp?.id === app.id
                          ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700'
                      }`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${
                            selectedApp?.id === app.id
                              ? 'bg-emerald-100 text-emerald-600 dark:bg-emerald-800/50 dark:text-emerald-400'
                              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'
                          }`}>
                            {getLoanTypeIcon(app.loan_type)}
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {loanTypes.find(t => t.id === app.loan_type)?.name || app.loan_type}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {formatCurrency(app.loan_amount)}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteApplication(app.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      </div>
                      {app.document_progress && (
                        <div className="mt-3">
                          <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                            <span>{t('documentsProgress')}</span>
                            <span>{app.document_progress.completed}/{app.document_progress.total}</span>
                          </div>
                          <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full">
                            <div
                              className="h-2 bg-emerald-500 rounded-full"
                              style={{ width: `${app.document_progress.percentage}%` }}
                            />
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Application Details */}
          <div className="lg:col-span-2">
            {selectedApp ? (
              <div className="space-y-6">
                {/* Application Overview */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('applicationOverview')}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('loanAmount')}</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(selectedApp.loan_amount)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('annualIncome')}</p>
                      <p className="text-lg font-semibold text-gray-900 dark:text-white">
                        {formatCurrency(selectedApp.annual_income)}
                      </p>
                    </div>
                    {selectedApp.current_dti != null && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('dti')}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {selectedApp.current_dti.toFixed(1)}%
                        </p>
                      </div>
                    )}
                    {selectedApp.ltv != null && (
                      <div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('ltv')}</p>
                        <p className="text-lg font-semibold text-gray-900 dark:text-white">
                          {selectedApp.ltv.toFixed(1)}%
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Document Checklist */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <ClipboardDocumentCheckIcon className="h-5 w-5 mr-2 text-emerald-500" />
                    {t('documentChecklist')}
                  </h3>
                  <div className="space-y-4">
                    {checklist.map((category) => (
                      <div key={category.id} className="border border-gray-200 dark:border-gray-700 rounded-lg">
                        <button
                          onClick={() => toggleCategory(category.id)}
                          className="w-full flex items-center justify-between p-4 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
                        >
                          <span className="font-medium text-gray-900 dark:text-white">
                            {category.name}
                          </span>
                          <div className="flex items-center space-x-2">
                            <span className="text-sm text-gray-500 dark:text-gray-400">
                              {category.items.filter(i => i.status === 'complete').length}/{category.items.length}
                            </span>
                            {expandedCategories.includes(category.id) ? (
                              <ChevronDownIcon className="h-5 w-5 text-gray-400" />
                            ) : (
                              <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                            )}
                          </div>
                        </button>
                        {expandedCategories.includes(category.id) && (
                          <div className="px-4 pb-4 space-y-2">
                            {category.items.map((item) => (
                              <div
                                key={item.id}
                                className="flex items-center justify-between py-2 px-3 bg-gray-50 dark:bg-gray-700/30 rounded-lg"
                              >
                                <div className="flex items-center space-x-3">
                                  <button
                                    onClick={() => handleUpdateDocumentStatus(
                                      item.id,
                                      item.status === 'complete' ? 'not_started' : 'complete'
                                    )}
                                    className={`flex-shrink-0 ${getStatusColor(item.status || 'not_started')}`}
                                  >
                                    {item.status === 'complete' ? (
                                      <CheckCircleIcon className="h-5 w-5" />
                                    ) : item.status === 'in_progress' ? (
                                      <ClockIcon className="h-5 w-5" />
                                    ) : (
                                      <div className="h-5 w-5 border-2 border-current rounded-full" />
                                    )}
                                  </button>
                                  <div>
                                    <span className={`text-sm ${
                                      item.status === 'complete' ? 'text-gray-500 line-through' : 'text-gray-900 dark:text-white'
                                    }`}>
                                      {item.name}
                                    </span>
                                    {item.required && (
                                      <span className="ml-2 text-xs text-red-500">*</span>
                                    )}
                                  </div>
                                </div>
                                <select
                                  value={item.status || 'not_started'}
                                  onChange={(e) => handleUpdateDocumentStatus(item.id, e.target.value)}
                                  className="text-xs bg-transparent border-none focus:ring-0 text-gray-500 dark:text-gray-400"
                                >
                                  <option value="not_started">{t('statusNotStarted')}</option>
                                  <option value="in_progress">{t('statusInProgress')}</option>
                                  <option value="complete">{t('statusComplete')}</option>
                                  <option value="not_applicable">{t('statusNotApplicable')}</option>
                                </select>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Lender Comparison */}
                <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                      <ScaleIcon className="h-5 w-5 mr-2 text-emerald-500" />
                      {t('lenderComparison')}
                    </h3>
                    <Button onClick={() => setShowLenderModal(true)} className="flex items-center">
                      <PlusIcon className="h-4 w-4 mr-1" />
                      {t('addLender')}
                    </Button>
                  </div>

                  {lenderComparisons.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                      <ScaleIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>{t('noLenders')}</p>
                      <p className="text-sm mt-1">{t('addLendersToCompare')}</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="border-b border-gray-200 dark:border-gray-700">
                            <th className="text-left py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('lender')}</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('rate')}</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('monthlyPayment')}</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400">{t('totalCost')}</th>
                            <th className="text-right py-3 px-4 text-sm font-medium text-gray-500 dark:text-gray-400"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {lenderComparisons.map((lender, index) => (
                            <tr
                              key={lender.id}
                              className={`border-b border-gray-100 dark:border-gray-700/50 ${
                                index === 0 ? 'bg-emerald-50 dark:bg-emerald-900/20' : ''
                              }`}
                            >
                              <td className="py-3 px-4">
                                <div className="flex items-center space-x-2">
                                  {index === 0 && (
                                    <span className="text-xs bg-emerald-100 text-emerald-800 dark:bg-emerald-800 dark:text-emerald-200 px-2 py-0.5 rounded">
                                      {t('bestOption')}
                                    </span>
                                  )}
                                  <span className="font-medium text-gray-900 dark:text-white">
                                    {lender.lender_name}
                                  </span>
                                </div>
                              </td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                                {lender.interest_rate.toFixed(3)}%
                              </td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                                {formatCurrency(lender.monthly_payment)}
                              </td>
                              <td className="py-3 px-4 text-right text-gray-900 dark:text-white">
                                {formatCurrency(lender.total_cost)}
                              </td>
                              <td className="py-3 px-4 text-right">
                                <button
                                  onClick={() => handleDeleteLender(lender.id)}
                                  className="text-gray-400 hover:text-red-500"
                                >
                                  <TrashIcon className="h-4 w-4" />
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-12 text-center">
                <DocumentDuplicateIcon className="h-16 w-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                  {t('selectApplication')}
                </h3>
                <p className="text-gray-500 dark:text-gray-400">
                  {t('selectAppDescription')}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pre-Qualification Tab */}
      {activeTab === 'prequal' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('preQualificationCalculator')}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('loanType')}
                </label>
                <select
                  value={preQualForm.loan_type}
                  onChange={(e) => setPreQualForm({ ...preQualForm, loan_type: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  {loanTypes.map((type) => (
                    <option key={type.id} value={type.id}>{type.name}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('annualIncome')}
                </label>
                <input
                  type="number"
                  value={preQualForm.annual_income}
                  onChange={(e) => setPreQualForm({ ...preQualForm, annual_income: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="75000"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('monthlyDebtPayments')}
                </label>
                <input
                  type="number"
                  value={preQualForm.monthly_debt_payments}
                  onChange={(e) => setPreQualForm({ ...preQualForm, monthly_debt_payments: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('creditScore')} ({t('optional')})
                </label>
                <input
                  type="number"
                  value={preQualForm.credit_score}
                  onChange={(e) => setPreQualForm({ ...preQualForm, credit_score: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="720"
                  min="300"
                  max="850"
                />
              </div>
              {(preQualForm.loan_type.includes('mortgage') || preQualForm.loan_type.includes('home')) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('propertyValue')} ({t('optional')})
                    </label>
                    <input
                      type="number"
                      value={preQualForm.property_value}
                      onChange={(e) => setPreQualForm({ ...preQualForm, property_value: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="400000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('downPayment')} ({t('optional')})
                    </label>
                    <input
                      type="number"
                      value={preQualForm.down_payment}
                      onChange={(e) => setPreQualForm({ ...preQualForm, down_payment: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="80000"
                    />
                  </div>
                </>
              )}
              <Button onClick={handlePreQualification} className="w-full">
                <CalculatorIcon className="h-4 w-4 mr-2" />
                {t('calculatePreQual')}
              </Button>
            </div>
          </div>

          {/* Pre-Qual Results */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('estimatedResults')}
            </h2>
            {preQualResult ? (
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg">
                    <p className="text-sm text-emerald-600 dark:text-emerald-400">{t('maxLoanAmount')}</p>
                    <p className="text-2xl font-bold text-emerald-700 dark:text-emerald-300">
                      {formatCurrency(preQualResult.max_loan_amount)}
                    </p>
                  </div>
                  <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                    <p className="text-sm text-blue-600 dark:text-blue-400">{t('estimatedRate')}</p>
                    <p className="text-2xl font-bold text-blue-700 dark:text-blue-300">
                      {preQualResult.estimated_rate.toFixed(3)}%
                    </p>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                    {t('approvalFactors')}
                  </h4>
                  <div className="space-y-2">
                    {preQualResult.approval_factors.map((factor, index) => (
                      <div
                        key={index}
                        className={`p-3 rounded-lg ${getApprovalStatusColor(factor.status)}`}
                      >
                        <div className="flex items-center justify-between">
                          <span className="font-medium">{factor.factor}</span>
                          <span className="text-sm">{factor.message}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('recommendations')}
                  </h4>
                  <ul className="list-disc list-inside text-sm text-gray-600 dark:text-gray-400 space-y-1">
                    {preQualResult.recommendations.map((rec, index) => (
                      <li key={index}>{rec}</li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <CalculatorIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('enterInfoForPreQual')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Letters Tab */}
      {activeTab === 'letters' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
              {t('letterTemplates')}
            </h2>
            <div className="space-y-3">
              {letterTemplates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => {
                    setSelectedLetterType(template.id);
                    setLetterFields({});
                    setGeneratedLetter(null);
                    setLetterError(null);
                  }}
                  className={`w-full text-left p-4 rounded-lg border transition-colors ${
                    selectedLetterType === template.id
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20'
                      : 'border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <DocumentTextIcon className={`h-5 w-5 ${
                      selectedLetterType === template.id ? 'text-emerald-600' : 'text-gray-400'
                    }`} />
                    <div>
                      <p className="font-medium text-gray-900 dark:text-white">{template.name}</p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{template.description}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6">
            {selectedLetterType ? (
              generatedLetter ? (
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {t('generatedLetter')}
                    </h3>
                    {/* Icon bar: Edit, Download, Clear */}
                    <div className="flex items-center space-x-1">
                      <button
                        onClick={() => setGeneratedLetter(null)}
                        className="p-2 text-gray-500 hover:text-emerald-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title={t('editFields')}
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      <ExportDropdownMenu
                        onExport={handleLetterExport}
                        onPrint={handleLetterPrint}
                        showPrint={true}
                      />
                      <button
                        onClick={handleClearLetterForm}
                        className="p-2 text-gray-500 hover:text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                        title={t('common:buttons.clear')}
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                  <pre className="whitespace-pre-wrap text-sm bg-gray-50 dark:bg-gray-900 p-4 rounded-lg overflow-auto max-h-96 font-mono text-gray-800 dark:text-gray-200">
                    {generatedLetter}
                  </pre>
                </div>
              ) : (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                    {t('fillInDetails')}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                    {t('fillFieldsDescription')}
                  </p>
                  {/* Common fields based on letter type */}
                  {selectedLetterType === 'employment_verification' && (
                    <div className="space-y-3">
                      {['employee_name', 'company_name', 'start_date', 'position', 'employment_status', 'annual_salary', 'additional_compensation', 'hr_name', 'hr_title', 'contact_phone', 'contact_email'].map(field => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
                            {field.replace(/_/g, ' ')}
                          </label>
                          {renderLetterField(field)}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedLetterType === 'gift_letter' && (
                    <div className="space-y-3">
                      {['donor_name', 'recipient_name', 'recipient_address', 'gift_amount', 'gift_source', 'gift_purpose', 'donor_address', 'donor_phone', 'relationship'].map(field => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
                            {field.replace(/_/g, ' ')}
                          </label>
                          {renderLetterField(field)}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedLetterType === 'credit_explanation' && (
                    <div className="space-y-3">
                      {['borrower_name', 'credit_item', 'creditor_name', 'item_date', 'item_amount', 'explanation', 'current_status', 'resolution_steps', 'situation_type', 'borrower_address', 'borrower_phone'].map(field => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
                            {field.replace(/_/g, ' ')}
                          </label>
                          {renderLetterField(field, ['explanation', 'current_status', 'resolution_steps'])}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedLetterType === 'gap_explanation' && (
                    <div className="space-y-3">
                      {['borrower_name', 'gap_start_date', 'gap_end_date', 'gap_reason', 'activities_during_gap', 'current_employer', 'current_start_date', 'current_position', 'borrower_phone'].map(field => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
                            {field.replace(/_/g, ' ')}
                          </label>
                          {renderLetterField(field, ['gap_reason', 'activities_during_gap'])}
                        </div>
                      ))}
                    </div>
                  )}
                  {selectedLetterType === 'hardship_letter' && (
                    <div className="space-y-3">
                      {['lender_name', 'loan_number', 'property_address', 'request_type', 'monthly_income', 'monthly_expenses', 'current_payment', 'hardship_explanation', 'steps_taken', 'proposed_resolution', 'borrower_name', 'borrower_address', 'borrower_phone', 'borrower_email'].map(field => (
                        <div key={field}>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 capitalize">
                            {field.replace(/_/g, ' ')}
                          </label>
                          {renderLetterField(field, ['hardship_explanation', 'steps_taken', 'proposed_resolution'])}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Error message */}
                  {letterError && (
                    <div className="mt-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                      <p className="text-sm text-red-600 dark:text-red-400">{letterError}</p>
                    </div>
                  )}

                  <Button
                    onClick={handleGenerateLetter}
                    className="w-full mt-4"
                    disabled={generatingLetter}
                  >
                    {generatingLetter ? t('generating') : t('generateLetter')}
                  </Button>
                </div>
              )
            ) : (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <DocumentTextIcon className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>{t('selectLetterTemplate')}</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* New Application Modal */}
      {showNewAppModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('newApplication')}
                </h2>
                <button
                  onClick={() => setShowNewAppModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('loanType')} *
                  </label>
                  <select
                    value={newAppForm.loan_type}
                    onChange={(e) => setNewAppForm({ ...newAppForm, loan_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    <option value="">{t('selectLoanType')}</option>
                    {loanTypes.map((type) => (
                      <option key={type.id} value={type.id}>{type.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('loanAmount')} *
                  </label>
                  <input
                    type="number"
                    value={newAppForm.loan_amount}
                    onChange={(e) => setNewAppForm({ ...newAppForm, loan_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="250000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('annualIncome')} *
                  </label>
                  <input
                    type="number"
                    value={newAppForm.annual_income}
                    onChange={(e) => setNewAppForm({ ...newAppForm, annual_income: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="85000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('monthlyDebtPayments')}
                  </label>
                  <input
                    type="number"
                    value={newAppForm.monthly_debt_payments}
                    onChange={(e) => setNewAppForm({ ...newAppForm, monthly_debt_payments: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('creditScore')} ({t('optional')})
                  </label>
                  <input
                    type="number"
                    value={newAppForm.credit_score_estimate}
                    onChange={(e) => setNewAppForm({ ...newAppForm, credit_score_estimate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="720"
                    min="300"
                    max="850"
                  />
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button variant="secondary" onClick={() => setShowNewAppModal(false)}>
                    {t('common:buttons.cancel')}
                  </Button>
                  <Button
                    onClick={handleCreateApplication}
                    disabled={!newAppForm.loan_type || !newAppForm.loan_amount || !newAppForm.annual_income}
                  >
                    {t('createApplication')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Lender Modal */}
      {showLenderModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {t('addLender')}
                </h2>
                <button
                  onClick={() => setShowLenderModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('lenderName')} *
                  </label>
                  <input
                    type="text"
                    value={lenderForm.lender_name}
                    onChange={(e) => setLenderForm({ ...lenderForm, lender_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="Bank of America"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('interestRate')} (%) *
                    </label>
                    <input
                      type="number"
                      step="0.001"
                      value={lenderForm.interest_rate}
                      onChange={(e) => setLenderForm({ ...lenderForm, interest_rate: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="6.5"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('termMonths')} *
                    </label>
                    <input
                      type="number"
                      value={lenderForm.loan_term_months}
                      onChange={(e) => setLenderForm({ ...lenderForm, loan_term_months: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="360"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('fees')} ($)
                    </label>
                    <input
                      type="number"
                      value={lenderForm.fees}
                      onChange={(e) => setLenderForm({ ...lenderForm, fees: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="3000"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {t('points')} (%)
                    </label>
                    <input
                      type="number"
                      step="0.125"
                      value={lenderForm.points}
                      onChange={(e) => setLenderForm({ ...lenderForm, points: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="0.5"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button variant="secondary" onClick={() => setShowLenderModal(false)}>
                    {t('common:buttons.cancel')}
                  </Button>
                  <Button
                    onClick={handleAddLender}
                    disabled={!lenderForm.lender_name || !lenderForm.interest_rate || !lenderForm.loan_term_months}
                  >
                    {t('addLender')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <div className="mt-8 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
        <p className="text-sm text-amber-800 dark:text-amber-200">
          {t('common:disclaimer.financial')}
        </p>
      </div>
    </div>
  );
};

export default LoanPlanning;
